import {
  createListenerMiddleware,
  isAnyOf,
  type AnyAction,
} from '@reduxjs/toolkit';
import { redactionWriteTaskId } from '../slices/editPageSlice';
import {
  sendEditorMetric,
  type EditorBoxAction,
} from 'src/utils/editorMetrics';
import { api } from './api';

/**
 * `editor_box_edited` for demo users: a redaction box was added or removed in
 * the routed document. Only manual single-box edits count. Bulk actions
 * (redact all, un-redact all, remove a page's suggestions, word-list sync of
 * many matching items) do not, because they would inflate box_adds and
 * box_removes. Sent from the fulfilled edit requests (so undo and redo count
 * too), at most once per task view per action (editorMetrics.ts). Only the
 * action is sent: no item, page, text or id.
 */
export const boxEditMetricsListener = createListenerMiddleware();

type BoxEditDto = { isRedact?: boolean } | undefined;

/** add / remove for a fulfilled edit request, or null when it is no box edit. */
export const boxEditAction = (action: AnyAction): EditorBoxAction | null => {
  const e = api.endpoints;
  const args = action.meta?.arg?.originalArgs;
  if (
    isAnyOf(
      e.tasksControllerAddCustomBox.matchFulfilled,
      e.tasksControllerAddItem.matchFulfilled
    )(action)
  ) {
    return 'add';
  }
  if (e.tasksControllerDeleteItem.matchFulfilled(action)) {
    return 'remove';
  }
  const dto: BoxEditDto = e.tasksControllerChangeRedactState.matchFulfilled(
    action
  )
    ? args?.changeRedactStateDto
    : undefined;
  if (dto && typeof dto.isRedact === 'boolean')
    return dto.isRedact ? 'add' : 'remove';
  return null;
};

boxEditMetricsListener.startListening({
  predicate: (action) => boxEditAction(action) !== null,
  effect: (action, { getState }) => {
    const state = getState() as {
      auth: { user: any; demoSource: any };
      editPage: { routeTaskId: string | null };
    };
    const user = state.auth.user;
    if (!(user?.accountTier === 'DEMO' || Boolean(user?.demo))) return;
    const taskId = redactionWriteTaskId(
      (action as AnyAction).meta?.arg?.originalArgs
    );
    if (taskId !== undefined && taskId !== state.editPage.routeTaskId) return;
    const kind = boxEditAction(action as AnyAction);
    if (kind)
      sendEditorMetric('editor_box_edited', {
        action: kind,
        source: state.auth.demoSource,
      });
  },
});
